/**
 * WebUSB Thermal Print Service
 * Updated: Logo loaded from URL instead of embedded base64 to prevent truncation issues.
 * Added: Graceful error handling so receipts print even if logo fails.
 */

import logoImage from "@/assets/output-onlinepngtools.png";
import image2 from "@/assets/medi.png";

class ThermalPrintService {
  static BUILD_VERSION = "2026-05-06-add-father-husband-name-v1";

  constructor() {
    this.isConnected = false;
    this.device = null;
    this.endpoint = null;
    this.deviceName = "";
    this.interfaceNumber = undefined;

    // Logo URL - can be configured via setLogoUrl()
    this.logoUrl = logoImage;
    this.logoUrl2 = image2;

    // Cached logo image data (to avoid reloading every print)
    this.cachedLogoData = null;
    this.logoLoadAttempted = false;
    this.buildVersion = ThermalPrintService.BUILD_VERSION;
  }

  static getInstance() {
    if (
      !ThermalPrintService.instance ||
      ThermalPrintService.instance.buildVersion !==
        ThermalPrintService.BUILD_VERSION
    ) {
      ThermalPrintService.instance = new ThermalPrintService();
    }
    return ThermalPrintService.instance;
  }

  /**
   * Configure the logo URL
   * @param {string} url - URL to the logo image (PNG recommended)
   */
  setLogoUrl(url) {
    this.logoUrl = url;
    this.cachedLogoData = null; // Clear cache when URL changes
    this.logoLoadAttempted = false;
  }

  /**
   * Alternatively, set logo from base64 string (with validation)
   * @param {string} base64String - Complete base64 encoded image
   */
  setLogoBase64(base64String) {
    if (!base64String || base64String.length < 100) {
      console.warn("[ThermalPrint] Invalid base64 string provided");
      return false;
    }
    this.logoUrl = `data:image/png;base64,${base64String}`;
    this.cachedLogoData = null;
    this.logoLoadAttempted = false;
    return true;
  }

  isSupported() {
    if (this.isMockMode()) return true;
    if (typeof window === "undefined" || typeof navigator === "undefined") {
      return false;
    }
    return "usb" in navigator;
  }

  isMockMode() {
    if (typeof localStorage === "undefined") return false;
    if (localStorage.getItem("thermal_printer_mock") === "false") return false;
    if (localStorage.getItem("thermal_printer_mock") === "true") return true;
    return process.env.NODE_ENV === "development";
  }

  static enableMockMode() {
    localStorage.setItem("thermal_printer_mock", "true");
    localStorage.setItem("printer_type", "Thermal");
    const instance = ThermalPrintService.getInstance();
    instance._mockConnect();
    window.dispatchEvent(
      new CustomEvent("thermal-printer-mock-changed", {
        detail: {
          connected: true,
          name: instance.deviceName
        }
      })
    );
    console.info(
      "[ThermalPrint Mock] Enabled. Printer shows as connected. Receipts log to console."
    );
    return instance;
  }

  static disableMockMode() {
    localStorage.setItem("thermal_printer_mock", "false");
    const instance = ThermalPrintService.getInstance();
    instance.isConnected = false;
    instance.device = null;
    instance.endpoint = null;
    instance.deviceName = "";
    instance._mockPrintBuffer = "";
    window.dispatchEvent(
      new CustomEvent("thermal-printer-mock-changed", {
        detail: { connected: false, name: "" }
      })
    );
    console.info("[ThermalPrint Mock] Disabled.");
    return instance;
  }

  _mockConnect() {
    this.isConnected = true;
    this.deviceName = "Mock Thermal Printer (Dev)";
    this.device = { opened: true, mock: true };
    this.endpoint = { endpointNumber: 1, direction: "out", type: "bulk" };
    this.interfaceNumber = 0;
    return this.deviceName;
  }

  _decodePrintData(data) {
    const bytes =
      typeof data === "string" ? new TextEncoder().encode(data) : data;
    return Array.from(bytes)
      .filter(byte => byte === 10 || byte === 13 || (byte >= 32 && byte <= 126))
      .map(byte => String.fromCharCode(byte))
      .join("");
  }

  _mockPrint(data) {
    const chunk = this._decodePrintData(data);
    this._mockPrintBuffer = (this._mockPrintBuffer || "") + chunk;

    if (chunk.includes("\x1dV") || chunk.includes("PATIENT COPY")) {
      console.group("[ThermalPrint Mock] Receipt");
      console.log(this._mockPrintBuffer.trim());
      console.groupEnd();
      this._mockPrintBuffer = "";
    }

    return Promise.resolve();
  }

  isSecureContext() {
    if (typeof window === "undefined") return false;
    return window.isSecureContext;
  }

  async autoConnect() {
    if (this.isMockMode()) {
      this._mockConnect();
      return true;
    }
    if (!this.isSupported()) return false;
    try {
      const devices = await navigator.usb.getDevices();
      if (devices.length > 0) {
        this.device = devices[0];
        this.deviceName = this.device.productName || "USB Printer";
        await this.connect();
        return true;
      }
    } catch (err) {
      console.warn("[ThermalPrint] Auto-connect failed:", err);
    }
    return false;
  }

  async requestDevice() {
    if (this.isMockMode()) {
      this._mockConnect();
      return this.deviceName;
    }
    if (!this.isSupported()) {
      throw new Error("WebUSB is not supported in this browser.");
    }
    try {
      const device = await navigator.usb.requestDevice({
        filters: [{ classCode: 0x07 }] // USB Printer class
      });
      this.device = device;
      this.deviceName = device.productName || "USB Printer";
      await this.connect();
      return this.deviceName;
    } catch (err) {
      console.error("[ThermalPrint] Request device failed:", err);
      throw err;
    }
  }

  async connect() {
    if (this.isMockMode()) {
      this._mockConnect();
      return;
    }
    if (!this.device) return;
    try {
      if (!this.device.opened) {
        await this.device.open();
      }

      await this.device.selectConfiguration(1);

      const configuration = this.device.configuration;
      for (const iface of configuration.interfaces) {
        try {
          await this.device.claimInterface(iface.interfaceNumber);

          for (const alternate of iface.alternates) {
            for (const endpoint of alternate.endpoints) {
              if (endpoint.direction === "out" && endpoint.type === "bulk") {
                this.endpoint = endpoint;
                this.isConnected = true;
                this.interfaceNumber = iface.interfaceNumber;
                return;
              }
            }
          }
        } catch (err) {
          console.warn("[ThermalPrint] Could not claim interface:", err);
        }
      }
      throw new Error("Could not find print endpoint.");
    } catch (err) {
      console.error("[ThermalPrint] Connect failed:", err);
      this.isConnected = false;
      throw err;
    }
  }

  async disconnect() {
    if (this.isMockMode()) {
      this.isConnected = false;
      this.device = null;
      this.endpoint = null;
      this.deviceName = "";
      return;
    }
    if (this.device && this.device.opened) {
      if (this.interfaceNumber !== undefined) {
        try {
          await this.device.releaseInterface(this.interfaceNumber);
        } catch (e) {
          /* ignore */
        }
      }
      await this.device.close();
    }
    this.device = null;
    this.endpoint = null;
    this.isConnected = false;
  }

  /**
   * Universal print method with Flow Control (Delays).
   */
  async print(data) {
    if (this.isMockMode()) {
      return this._mockPrint(data);
    }
    if (!this.isConnected) {
      await this.connect();
    }
    if (!this.device || !this.endpoint) {
      throw new Error("Printer not connected.");
    }

    // Convert strings to Uint8Array, but keep Uint8Arrays as-is
    const uint8Array =
      typeof data === "string" ? new TextEncoder().encode(data) : data;

    // Chunk size: 64 bytes is safe for most WebUSB endpoints
    const chunkSize = 64;
    for (let i = 0; i < uint8Array.length; i += chunkSize) {
      const chunk = uint8Array.slice(i, i + chunkSize);
      await this.device.transferOut(this.endpoint.endpointNumber, chunk);

      // Small delay to prevent printer buffer overflow
      await new Promise(resolve => setTimeout(resolve, 10));
    }
  }

  /**
   * Load and cache the logo image
   * @returns {Promise<{imageData: ImageData, width: number, height: number} | null>}
   */
  async loadLogo() {
    // Return cached data if available
    if (this.cachedLogoData) {
      return this.cachedLogoData;
    }

    // Don't retry if we already failed
    if (this.logoLoadAttempted && !this.cachedLogoData) {
      return null;
    }

    this.logoLoadAttempted = true;

    if (!this.logoUrl) {
      console.warn("[ThermalPrint] No logo URL configured");
      return null;
    }

    try {
      const img = new Image();
      img.crossOrigin = "anonymous"; // Enable CORS for external URLs

      // Create a promise that handles both load and error
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error("Logo load timeout"));
        }, 5000); // 5 second timeout

        img.onload = () => {
          clearTimeout(timeout);
          resolve();
        };

        img.onerror = () => {
          clearTimeout(timeout);
          reject(new Error("Failed to load logo image"));
        };

        img.src = this.logoUrl;
      });

      // Verify image loaded correctly
      if (!img.complete || img.naturalWidth === 0) {
        console.warn("[ThermalPrint] Logo image invalid or empty");
        return null;
      }

      // Create canvas and process image
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // Balanced width for readability without oversized logo.
      const targetWidth = 240;
      canvas.width = targetWidth;
      canvas.height = Math.floor(
        img.naturalHeight * (targetWidth / img.naturalWidth)
      );

      // White background (thermal printers print black on white)
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const threshold = this._calculateAutoThreshold(imageData.data);

      // Cache the processed data
      this.cachedLogoData = {
        imageData: imageData,
        width: canvas.width,
        height: canvas.height,
        pixels: imageData.data,
        threshold
      };

      console.log("[ThermalPrint] Logo loaded and cached successfully");
      return this.cachedLogoData;
    } catch (err) {
      console.warn("[ThermalPrint] Logo load failed:", err.message);
      return null;
    }
  }

  /**
   * Preload the logo (call this early, e.g., on app init)
   */
  async preloadLogo() {
    return await this.loadLogo();
  }

  /**
   * Clear the cached logo (useful if logo file is updated)
   */
  clearLogoCache() {
    this.cachedLogoData = null;
    this.logoLoadAttempted = false;
  }

  _getLuminance(r, g, b) {
    return 0.299 * r + 0.587 * g + 0.114 * b;
  }

  _calculateAutoThreshold(pixels) {
    let total = 0;
    let count = 0;

    // Sample every 4th pixel for stable auto-threshold.
    for (let i = 0; i < pixels.length; i += 16) {
      total += this._getLuminance(pixels[i], pixels[i + 1], pixels[i + 2]);
      count += 1;
    }

    const avg = count ? total / count : 128;
    // Slightly lower threshold keeps thin text darker on thermal heads.
    return Math.max(90, Math.min(170, avg - 18));
  }

  /**
   * Processes the logo and sends ESC/POS bit image commands using ESC * m=0 (8-bit single density).
   * This is the simplest and most widely compatible image printing mode.
   */
  async printLogo() {
    if (!this.isConnected) await this.connect();

    try {
      const logoData = await this.loadLogo();

      if (!logoData) {
        console.log("[ThermalPrint] No logo available, skipping logo print");
        return;
      }

      const { pixels, width, height, threshold = 128 } = logoData;

      // Use ESC * m=0 (8-bit single-density dot graphics)
      // Format: ESC * m nL nH [d1 d2 ...]
      // For m=0: each column is 1 byte (8 dots vertically)

      // Center alignment
      await this.print("\x1Ba\x01");

      // Set line spacing to 8 dots (no gap between rows)
      await this.print("\x1B3\x08");

      // Process in 8-row strips
      for (let stripY = 0; stripY < height; stripY += 8) {
        // ESC * 0 nL nH
        const nL = width & 0xff;
        const nH = (width >> 8) & 0xff;
        const header = new Uint8Array([0x1b, 0x2a, 0, nL, nH]);

        // Prepare data: 1 byte per column
        const stripData = new Uint8Array(width);

        for (let x = 0; x < width; x++) {
          let byte = 0;
          for (let bit = 0; bit < 8; bit++) {
            const y = stripY + bit;
            if (y < height) {
              const pixelIndex = (y * width + x) * 4;
              const r = pixels[pixelIndex];
              const g = pixels[pixelIndex + 1];
              const b = pixels[pixelIndex + 2];
              const luminance = this._getLuminance(r, g, b);
              if (luminance < threshold) {
                byte |= 0x80 >> bit;
              }
            }
          }
          stripData[x] = byte;
        }

        await this.print(header);
        await this.print(stripData);
        await this.print("\n");
      }

      // Reset line spacing to default
      await this.print("\x1B2");
      // Reset alignment
      await this.print("\x1Ba\x00");

      console.log("[ThermalPrint] Logo printed successfully");
    } catch (err) {
      console.warn("[ThermalPrint] Logo printing failed:", err.message);
      // Don't throw - allow receipt to continue without logo
    }
  }

  /**
   * Helper to load image from URL
   */
  _loadImage(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = e => reject(e);
      img.src = url;
    });
  }

  /**
   * Prints two logos side-by-side (Dual Logo Mode)
   * Uses Double Density (120dpi horizontal) to fit two logos.
   */
  async printDualLogo() {
    if (!this.isConnected) await this.connect();

    try {
      if (!this.logoUrl) return;

      const img = await this._loadImage(this.logoUrl);
      let img2 = null;
      if (this.logoUrl2) {
        try {
          img2 = await this._loadImage(this.logoUrl2);
        } catch (e) {
          console.warn("[ThermalPrint] Second logo failed to load", e);
        }
      }

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // Target settings for Double Density (m=1)
      // Horizontal resolution: 120 DPI.
      // 80mm paper (~72mm print width) = ~340-576 dots depending on printer.
      // Safe width: 360 dots = 3.0 inches.
      const totalWidth = 560;
      const logoWidth = 270; // Wider logos
      const gap = 20;

      // Vertical resolution: 60 DPI (standard).
      // To keep aspect ratio 1:1, we need to correct for the 120H x 60V pixel density.
      // A square printed image needs 2x horizontal pixels for every 1 vertical pixel.
      // So we draw the image "wide" (or short?) on the canvas.
      // Width on canvas: 170 pixels. (Will cover 170 dots).
      // Height on canvas:
      // H_dots / 60 = W_dots / 120 * Aspect
      // If Aspect (H/W) is same as source.
      // H_dots = (W_dots / 2) * (SourceH / SourceW)
      const visibleHeight =
        (logoWidth / 2) * (img.naturalHeight / img.naturalWidth);
      const h = Math.round(visibleHeight);

      canvas.width = totalWidth;
      canvas.height = h;

      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, totalWidth, h);
      ctx.imageSmoothingEnabled = false;

      // Draw Left
      ctx.drawImage(img, 0, 0, logoWidth, h);

      // Draw Right
      if (img2) {
        ctx.drawImage(img2, logoWidth + gap, 0, logoWidth, h);
      } else {
        ctx.drawImage(img, logoWidth + gap, 0, logoWidth, h);
      }

      const dualImageData = ctx.getImageData(0, 0, totalWidth, h);
      const { width, height } = dualImageData;
      const pixels = dualImageData.data;
      const threshold = this._calculateAutoThreshold(pixels);

      // Print using m=1 (Double Density - 120 DPI)

      // Center alignment
      await this.print("\x1Ba\x01");

      // Set line spacing to 8 dots
      await this.print("\x1B3\x08");

      for (let stripY = 0; stripY < height; stripY += 8) {
        // ESC * 1 nL nH (1 = Double Density)
        const nL = width & 0xff;
        const nH = (width >> 8) & 0xff;
        const header = new Uint8Array([0x1b, 0x2a, 1, nL, nH]);
        const stripData = new Uint8Array(width);

        for (let x = 0; x < width; x++) {
          let byte = 0;
          for (let bit = 0; bit < 8; bit++) {
            const y = stripY + bit;
            if (y < height) {
              const pixelIndex = (y * width + x) * 4;
              const r = pixels[pixelIndex];
              const g = pixels[pixelIndex + 1];
              const b = pixels[pixelIndex + 2];
              const luminance = this._getLuminance(r, g, b);
              if (luminance < threshold) {
                byte |= 0x80 >> bit;
              }
            }
          }
          stripData[x] = byte;
        }

        await this.print(header);
        await this.print(stripData);
        await this.print("\n");
      }

      // Reset
      await this.print("\x1B2");
      await this.print("\x1Ba\x00");

      console.log(
        "[ThermalPrint] Dual logo printed successfully (Double Density)"
      );
    } catch (err) {
      console.warn("[ThermalPrint] Dual logo printing failed:", err.message);
    }
  }

  formatReceipt(data, copyType) {
    const ESC = "\x1B";
    const GS = "\x1D";
    let cmds = "";

    // Initialize printer
    cmds += ESC + "@";

    // Paper width (80mm is typically 48 chars)
    const PAPER_WIDTH = 48;
    const LINE = "-".repeat(PAPER_WIDTH);
    const DOUBLE_LINE = "=".repeat(PAPER_WIDTH);

    // Organization Name - Centered
    cmds += ESC + "a" + "\x01";

    // Service + token header
    cmds += GS + "!" + "\x11";
    cmds += `${data.serviceHeader || "TOKEN"}\n`;
    cmds += GS + "!" + "\x00";
    cmds += ESC + "E" + "\x01";
    cmds += `TOKEN NO : ${data.token || "-"}\n\n`;
    cmds += ESC + "E" + "\x00";

    // Header Info - Left aligned
    cmds += ESC + "a" + "\x00";
    cmds += `MR No: ${data.mrNo}\n`;
    cmds += `Patient: ${data.patientName}\n`;
    if (data.fatherHusbandName || data.fatherOrHusbandName) {
      cmds += `F/H Name: ${data.fatherHusbandName ||
        data.fatherOrHusbandName}\n`;
    }
    cmds += `Age/Gender: ${data.age}/${data.gender}\n`;
    cmds += `Invoice: ${data.invoiceNo}\n`;
    cmds += `Date: ${data.date}\n`;
    if (data.referredBy) {
      cmds += `Ref By: ${data.referredBy}\n`;
    }
    if (data.panelName) {
      cmds += `Panel: ${data.panelName}\n`;
      cmds += `Status: ${data.invoiceStatus === 1 ? "Paid" : "Unpaid"}\n`;
    }
    cmds += LINE + "\n";

    // Table Header
    cmds += ESC + "E" + "\x01";
    cmds +=
      "Service".padEnd(24) + "Price".padStart(10) + "Total".padStart(14) + "\n";
    cmds += ESC + "E" + "\x00";
    cmds += LINE + "\n";

    // Table Rows
    const rows = data.rows || [];
    rows.forEach(row => {
      const name = (row.service || "").substring(0, 24).padEnd(24);
      const price = String(row.price || 0).padStart(10);
      const total = String(row.subtotal || 0).padStart(14);
      cmds += `${name}${price}${total}\n`;
    });

    cmds += LINE + "\n";

    const formatTotalLine = (label, value) => {
      const valStr = String(value);
      const padding = PAPER_WIDTH - label.length - valStr.length;
      return label + " ".repeat(Math.max(1, padding)) + valStr + "\n";
    };

    cmds += formatTotalLine("GRAND TOTAL (PKR):", data.total);
    if (data.discount > 0) {
      cmds += formatTotalLine("DISCOUNT (PKR):", data.discount);
    }
    cmds += ESC + "E" + "\x01";
    cmds += formatTotalLine("NET TOTAL (PKR):", data.netTotal);
    cmds += ESC + "E" + "\x00";
    cmds += DOUBLE_LINE + "\n\n";

    // Footer - Centered
    cmds += ESC + "a" + "\x01";
    cmds += `Printed By: ${data.printedBy || "Staff"}\n`;
    cmds += `${data.printDate}\n`;
    cmds += "Powered by uConnect Technologies\n\n\n";

    // Copy Type (Footer)
    cmds += ESC + "E" + "\x01";
    cmds += `--- ${copyType} ---\n\n\n\n`;
    cmds += ESC + "E" + "\x00";

    // Cut Paper
    cmds += GS + "V" + "\x42" + "\x00";

    return cmds;
  }

  async _printFullCopy(data, label, dualLogo = false) {
    // Reset printer before starting new copy
    await this.print("\x1B@");

    // Print logo with graceful error handling
    try {
      if (dualLogo) {
        await this.printDualLogo();
      } else {
        await this.printLogo();
      }
    } catch (err) {
      console.warn("[ThermalPrint] Logo skipped:", err.message);
      // Continue without logo
    }

    const content = this.formatReceipt(data, label);
    await this.print(content);
  }

  async printRadiology(data) {
    await this._printFullCopy(data, "DOCTOR COPY");
    await this._printFullCopy(data, "PATIENT COPY");
  }

  async printLaboratory(data) {
    await this._printFullCopy(data, "LAB ASSISTANT COPY");
    await this._printFullCopy(data, "PATIENT COPY");
  }

  async printAppointment(data) {
    await this._printFullCopy(data, "DOCTOR COPY");
    await this._printFullCopy(data, "PATIENT COPY");
  }

  _recordThermalPrintForTests(data, type, options) {
    if (
      typeof window === "undefined" ||
      process.env.NODE_ENV !== "development"
    ) {
      return;
    }
    window.__lastThermalPrintData = JSON.parse(JSON.stringify(data));
    window.__printTestLog = window.__printTestLog || [];
    window.__printTestLog.push({
      kind: "thermal",
      data: window.__lastThermalPrintData,
      type,
      options,
      at: Date.now()
    });
  }

  async printCustom(data, type, options) {
    this._recordThermalPrintForTests(data, type, options);
    const dualLogo = options.dualLogo || false;
    if (options.printOfficial) {
      const officialLabel =
        type === "Laboratory" ? "LAB ASSISTANT COPY" : "DOCTOR COPY";
      await this._printFullCopy(data, officialLabel, dualLogo);
    }
    if (options.printPatient) {
      await this._printFullCopy(data, "PATIENT COPY", dualLogo);
    }
  }
}

export default ThermalPrintService;
